import React, { useCallback, useMemo } from 'react';

import { Menu, MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { MenuStore } from 'redux/reducers/MenuReducer';

import { MenuNode } from 'models/MenuNode';
import { User } from 'models/User';

import { MenuType } from '../../../config/Config';
import { DEFAULT_REDIRECT_AUTH_ROUTE } from '../../../config/Constants';
import MenuAction from '../../../redux/actions/menuActions/Actions';
import DynamicIcon from '../../DynamicIcon/DynamicIcon';

interface NavigationMenuTreeProps {
  style?: React.CSSProperties;
  className?: string;
  mode?: 'inline' | 'vertical' | 'horizontal';
}

type MenuItemDef = NonNullable<MenuProps['items']>[number];

const computePath = (node: MenuNode): string | null => {
  switch (node.menuTypeId) {
    case MenuType.IFRAME_VIEWER:
      return `/dynamic-viewer/${node.code}`;
    case MenuType.POWER_BI_REPORT:
      return `/pbi-viewer/${node.code}`;
    case MenuType.PDF_VIEWER:
      return `/pdf-viewer/${node.code}`;
    case MenuType.OFFICE_VIEWER:
      return `/office-viewer/${node.code}`;
    case MenuType.URL_REDIRECT:
      return `/download/${node.code}`;
    case MenuType.STANDARD_MENU:
      return node.redirectLink || DEFAULT_REDIRECT_AUTH_ROUTE;
    case MenuType.PARENT_MENU:
    default:
      return null;
  }
};

const renderIcon = (node: MenuNode, isParent: boolean) => {
  if (node.icon && node.icon.trim().length > 0) {
    return <DynamicIcon type={node.icon.trim()} />;
  }
  return isParent ? <DynamicIcon type={'SwitcherOutlined'} /> : null;
};

const NavigationMenuTree: React.FC<NavigationMenuTreeProps> = ({
  style,
  className,
  mode = 'inline',
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const authUser: User = useSelector((state: any) => state.auth);
  const selectedKeys = useSelector(
    (state: any) => (state.menu as MenuStore).selectedKeys
  );
  const openKeys = useSelector(
    (state: any) => (state.menu as MenuStore).openKeys
  );

  const menusTree: MenuNode[] = (authUser as any)?.menus || [];
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const homeLabel = t('common.home_label');

  const items = useMemo<MenuItemDef[]>(() => {
    const getLocalizedTitle = (node: MenuNode): string => {
      const localized = node.localizedTitles?.[lang];
      const candidate = typeof localized === 'string' ? localized || '' : '';
      const trimmed = candidate.trim();
      return trimmed.length > 0 ? trimmed : node.title;
    };

    const buildItem = (node: MenuNode): MenuItemDef => {
      const isParent =
        node.menuTypeId === MenuType.PARENT_MENU ||
        (node.children && node.children.length > 0);
      const icon = renderIcon(node, !!isParent);
      const title = getLocalizedTitle(node);

      if (isParent) {
        return {
          key: node.code,
          icon,
          label: title,
          children: (node.children || []).map(buildItem),
        } as MenuItemDef;
      }

      const path = computePath(node);
      return {
        key: node.code,
        icon,
        label: path ? <Link to={path}>{title}</Link> : <span>{title}</span>,
      } as MenuItemDef;
    };

    return [
      {
        key: 'HOME__PATH__UNSHIFT',
        icon: <DynamicIcon type="HomeOutlined" />,
        label: <Link to={DEFAULT_REDIRECT_AUTH_ROUTE}>{homeLabel}</Link>,
      },
      ...(menusTree || []).map(buildItem),
    ];
  }, [menusTree, lang, homeLabel]);

  const onOpenChange = useCallback(
    (openKeysUpdated: string[]) => {
      dispatch(MenuAction.update({ openKeys: openKeysUpdated }));
    },
    [dispatch]
  );

  return (
    <Menu
      mode={mode}
      style={style}
      className={className}
      selectedKeys={selectedKeys}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      items={items}
    />
  );
};

export default React.memo(NavigationMenuTree);
